import React from 'react';
import { FileText, Folder, Tag, Globe } from 'lucide-react';
import { CommandGroup, CommandItem } from '../../components/ui/command';
import { GlobalSearchTask } from './SearchProvider';

interface SearchResultGroupsProps {
  filteredResults: {
    activeTasks: GlobalSearchTask[];
    completedTasks: GlobalSearchTask[];
    projects: Array<any>;
    categories: Array<any>;
    contexts: Array<any>;
  };
  onSelectTask: (task: GlobalSearchTask) => void;
  onSelectProject: (project: any) => void;
  onSelectCategory: (category: any) => void;
  onSelectContext: (context: any) => void;
}

// Extract task item component
interface TaskItemProps {
  task: GlobalSearchTask;
  onSelect: () => void;
  isCompleted?: boolean;
}

function TaskItem({ task, onSelect, isCompleted = false }: TaskItemProps) {
  const titleClass = isCompleted ? 'line-through' : 'font-medium';
  const itemClass = `cursor-pointer ${isCompleted ? 'opacity-60' : ''}`;
  const descriptionClass = `text-sm text-muted-foreground line-clamp-1 ${isCompleted ? 'line-through' : ''}`;

  return (
    <CommandItem
      key={task._id}
      value={`${task.title} ${task.description || ''} ${task.contextName}`}
      onSelect={onSelect}
      className={itemClass}
    >
      <FileText className="mr-2 h-4 w-4" />
      <div className="flex flex-col flex-1">
        <div className="flex items-center gap-2">
          <span className={titleClass}>{task.title}</span>
          <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
            {task.contextName}
          </span>
        </div>
        {task.description && (
          <span className={descriptionClass}>
            {task.description}
          </span>
        )}
        {!isCompleted && <TaskMetadata task={task} />}
      </div>
    </CommandItem>
  );
}

// Extract task metadata to reduce complexity
function TaskMetadata({ task }: { task: GlobalSearchTask }) {
  return (
    <div className="flex gap-2 text-xs text-muted-foreground">
      {task.projectName && <span>📁 {task.projectName}</span>}
      {task.categoryName && <span>🏷️ {task.categoryName}</span>}
      {task.dueAt && (
        <span>📅 {new Date(task.dueAt).toLocaleDateString()}</span>
      )}
    </div>
  );
}

// Extract project item component
interface ProjectItemProps {
  project: any;
  onSelect: () => void;
}

function ProjectItem({ project, onSelect }: ProjectItemProps) {
  return (
    <CommandItem
      key={project._id}
      value={`${project.name} ${project.description || ''} ${project.contextName}`}
      onSelect={onSelect}
      className="cursor-pointer"
    >
      <Folder className="mr-2 h-4 w-4" />
      <div className="flex flex-col flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{project.name}</span>
          <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
            {project.contextName}
          </span>
        </div>
        {project.description && (
          <span className="text-sm text-muted-foreground line-clamp-1">
            {project.description}
          </span>
        )}
      </div>
    </CommandItem>
  );
}

export const SearchResultGroups: React.FC<SearchResultGroupsProps> = ({
  filteredResults,
  onSelectTask,
  onSelectProject,
  onSelectCategory,
  onSelectContext,
}) => {
  return (
    <>
      {filteredResults.activeTasks.length > 0 && (
        <CommandGroup heading="Tasks">
          {filteredResults.activeTasks.map((task) => (
            <TaskItem 
              key={task._id} 
              task={task} 
              onSelect={() => onSelectTask(task)} 
            />
          ))}
        </CommandGroup>
      )}

      {filteredResults.completedTasks.length > 0 && (
        <CommandGroup heading="Completed Tasks">
          {filteredResults.completedTasks.map((task) => (
            <TaskItem 
              key={task._id} 
              task={task} 
              onSelect={() => onSelectTask(task)} 
              isCompleted
            />
          ))}
        </CommandGroup>
      )}

      {filteredResults.projects.length > 0 && (
        <CommandGroup heading="Projects">
          {filteredResults.projects.map((project) => (
            <ProjectItem 
              key={project._id} 
              project={project} 
              onSelect={() => onSelectProject(project)} 
            />
          ))}
        </CommandGroup>
      )}

      {filteredResults.categories.length > 0 && (
        <CommandGroup heading="Categories">
          {filteredResults.categories.map((category) => (
            <CommandItem
              key={category._id}
              value={`${category.name} ${category.contextName}`}
              onSelect={() => onSelectCategory(category)}
              className="cursor-pointer"
            >
              <Tag className="mr-2 h-4 w-4" />
              <div className="flex items-center gap-2">
                <span className="font-medium">{category.name}</span>
                <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                  {category.contextName}
                </span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {filteredResults.contexts.length > 0 && (
        <CommandGroup heading="Contexts">
          {filteredResults.contexts.map((context) => (
            <CommandItem
              key={context._id}
              value={context.name}
              onSelect={() => onSelectContext(context)}
              className="cursor-pointer"
            >
              <Globe className="mr-2 h-4 w-4" />
              <div className="flex items-center gap-2">
                <span className="font-medium">{context.name}</span>
                {context.color && (
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: context.color }}
                  />
                )}
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      )}
    </>
  );
};